import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2, ShoppingBasket } from "lucide-react";
import { api, MODULES } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { checkout } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import { basket, useBasket, toBasketBody } from "@/lib/basket";
import { PageHero } from "@/pages/site/ContentPage";
import { OrderSummary } from "@/components/OrderSummary";
import { BundleSuggest } from "@/components/Related";
import { toast } from "sonner";

const label = (m) => MODULES.find((x) => x.id === m)?.label || m;

function Row({ title, sub, onRemove, testId }) {
  return (
    <div className="flex items-center gap-4 border border-slate-800 rounded-md px-4 py-3 bg-[#0F172A]/70" data-testid={testId}>
      <div className="flex-1 min-w-0"><p className="font-medium truncate">{title}</p><p className="text-xs text-slate-400">{sub}</p></div>
      <button onClick={onRemove} data-testid={`${testId}-remove`} className="p-2 text-slate-500 hover:text-red-400" title="Remove"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export default function Basket() {
  const b = useBasket();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { currency, country } = useCurrency();
  const [quote, setQuote] = useState(null);
  const [agree, setAgree] = useState(false);
  const [billing, setBilling] = useState({});

  useEffect(() => {
    if (!b.count) return setQuote(null);
    api.post("/checkout/basket-quote", toBasketBody(b)).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.subs, b.reports, b.count, user]);

  const done = () => { basket.clear(); qc.invalidateQueries(); nav("/account"); };
  const pay = () => (user ? checkout({ type: "basket", accept_terms: agree, currency, billing: { ...billing, country }, basket: toBasketBody(b) }, done) : nav("/login?next=/basket"));

  return (
    <div>
      <PageHero eyebrow="Basket" title="Your basket" subtitle="Dashboard subscriptions from any module plus Special Reports, all in one payment and one invoice." />
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-12 grid lg:grid-cols-[1fr_340px] gap-10">
        <div className="space-y-3" data-testid="basket-items">
          {!b.count && (
            <div className="text-center py-16 border border-dashed border-slate-800 rounded-lg" data-testid="basket-empty">
              <ShoppingBasket className="h-10 w-10 mx-auto text-slate-600" />
              <p className="text-slate-400 mt-3">Your basket is empty.</p>
              <div className="flex gap-3 justify-center mt-5"><Link to="/dashboards" className="px-4 py-2 rounded-md bg-[#1074BA] text-sm">Browse dashboards</Link><Link to="/reports" className="px-4 py-2 rounded-md border border-slate-700 text-sm">Browse reports</Link></div>
            </div>
          )}
          {b.subs.map((s) => <Row key={`${s.module}-${s.product}`} testId={`basket-sub-${s.module}-${s.product}`} title={s.product} sub={`${label(s.module)} · ${s.regions.join(", ")} · 12 months`} onRemove={() => basket.removeSub(s.module, s.product)} />)}
          {b.reports.map((r) => <Row key={r.id} testId={`basket-report-${r.id}`} title={r.title} sub="Special Report · single-user licence" onRemove={() => basket.removeReport(r.id)} />)}
          {b.subs.length > 0 && <BundleSuggest subs={toBasketBody(b).subs} count={quote?.count} nextTier={quote?.next_tier} onAdd={(s) => { basket.addSubs(s.module, [s.product], ["Global"]); toast.success(`${s.product} · ${s.label} added`); }} />}
          {b.count > 0 && <button onClick={() => basket.clear()} data-testid="basket-clear" className="text-xs text-slate-500 hover:text-red-400">Clear basket</button>}
        </div>
        {b.count > 0 && <div className="self-start lg:sticky lg:top-24">
          <OrderSummary quote={quote} user={user} billing={billing} setBilling={setBilling} agree={agree} setAgree={setAgree} country={country}
            onPay={pay} payLabel={user ? "Pay & activate" : "Login / Register to checkout"} empty="Add dashboards or reports to see the total." />
        </div>}
      </div>
    </div>
  );
}
